import { Dialog, Transition } from "@headlessui/react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Shield,
  Users,
  FileBadge2,
  Brain,
  Settings2,
  Image,
  ImagePlus,
  Building2,
  Settings,
  GitBranch,
  Zap,
} from "lucide-react";
import React, { Fragment, useState, useRef, useEffect } from "react";
import SecurityManagement from "./SettingsComponents/SecurityManagement";
import UserManagement from "./SettingsComponents/UserManagement";
import { useUser } from "../../context/UserContext";
import LicenseManagement from "./SettingsComponents/LicenseManagement";
import LargeLanguageModel from "./SettingsComponents/LargeLanguageModel";
import ConnectorManagement from "./SettingsComponents/ConnectorManagement";
import ClientLogoManagement from "./SettingsComponents/ClientLogoManagement";
import DepartmentManagement from "./SettingsComponents/DepartmentManagement";
import ApplicationSettings from "./SettingsComponents/ApplicationSettings";
import VersionManagement from "./SettingsComponents/VersionManagementComponents/VersionManagement";
import EventBirdgeSchedule from "../Settings/SettingsComponents/EventBridgeSchedule";
import DataSourceManagement from "../Settings/SettingsComponents/DataSourceManagement";

const categories = [
  { name: "Security", component: SecurityManagement, icon: Shield },
  { name: "Users", component: UserManagement, icon: Users },
  { name: "Departments", component: DepartmentManagement, icon: Building2 },
  {
    name: "User Permissions",
    component: ApplicationSettings,
    icon: Settings,
  },
  {
    name: "Licenses",
    component: LicenseManagement,
    icon: FileBadge2,
  },
  {
    name: "LLMs",
    component: LargeLanguageModel,
    icon: Brain,
  },
  {
    name: "Connectors",
    component: ConnectorManagement,
    icon: Settings2,
  },
  {
    name: "Client Logo",
    component: ClientLogoManagement,
    icon: ImagePlus,   
  },
  {
    name: "Version",
    component: VersionManagement,
    icon: GitBranch,
  },
  {
    name: "Actions",
    component: DataSourceManagement,
    icon: Zap,
  },
];

const SettingsDialog = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const { userDetails } = useUser();
  const [activeTab, setActiveTab] = useState("Security"); 
  const tabsRef = useRef<HTMLDivElement>(null);

  const filteredCategories =
    userDetails?.role === "admin"
      ? categories
      : []; 

  useEffect(() => {
    if (tabsRef.current) {
      const activeTabElement = tabsRef.current.querySelector(
        `[data-tab="${activeTab}"]`
      );
      if (activeTabElement) {
        activeTabElement.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    }
  }, [activeTab]);

  useEffect(() => {
    // Set default active tab when categories change
    if (filteredCategories.length > 0 && !filteredCategories.some(cat => cat.name === activeTab)) {
      setActiveTab(filteredCategories[0].name);
    }
  }, [filteredCategories, activeTab]);

  const scrollTabs = (direction: "left" | "right") => {
    if (tabsRef.current) {
      const scrollAmount = tabsRef.current.clientWidth / 2;
      tabsRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => setIsOpen(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 scrim" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative bg-surface-overlay text-primary rounded-2xl px-12 py-6 text-left align-middle shadow-xl ring-1 ring-black/5 dark:ring-gray-700 transition-all w-full lg:w-[1100px] sm:w-[90vw] h-[80vh] sm:h-[70vh] lg:h-[650px] overflow-hidden">
                <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-4">
                  <h2 className="text-xl font-semibold">Settings</h2>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="rounded-full bg-transparent hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 p-2"
                  >
                    <X size={20} />
                  </button>
                </div>
                {filteredCategories.length > 0 ? (
                  <div className="flex flex-col h-[calc(100%-4rem)] lg:flex-row">
                    <div className="relative lg:w-1/4 mb-4 lg:mb-0">
                      <button
                        onClick={() => scrollTabs("left")}
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 lg:hidden bg-white shadow-md rounded-full p-1 z-10"
                        style={{ transform: "translateY(-50%)" }}
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <div
                        ref={tabsRef}
                        className="flex lg:flex-col overflow-x-auto lg:overflow-y-auto lg:pr-4 scrollbar-hide"
                        style={{ padding: "0 2rem" }}
                      >
                        {filteredCategories.map(({ name, icon: Icon }) => (
                          <button
                            key={name}
                            data-tab={name}
                            onClick={() => setActiveTab(name)}
                            className={`rounded-full py-2 px-3 text-sm font-semibold text-gray-700 focus:outline-none text-left whitespace-nowrap mr-2 lg:mr-0 lg:mb-2 flex items-center ${
                              activeTab === name
                                ? "bg-gray-200"
                                : "hover:bg-gray-100"
                            }`}
                          >
                            <Icon width="18" height="18" />
                            <div className="pl-2">{name}</div>
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => scrollTabs("right")}
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 lg:hidden bg-white shadow-md rounded-full p-1 z-10"
                        style={{ transform: "translateY(-50%)" }}
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                    <div className="lg:w-3/4 overflow-y-auto flex-grow">
                      <div className="rounded-xl bg-surface-overlay shadow-sm h-full">
                        {filteredCategories.map(
                          ({ name, component: Component }) => {
                            return activeTab === name ? (
                              <Component key={name} />
                            ) : null;
                          }
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center items-center h-[calc(100%-4rem)]">
                    <p className="text-gray-500">Settings are only available for admin users.</p>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SettingsDialog;